import type { AudioPeaks } from '@tunasong/models'
import { useContainerSize, useDesignTokens, useMergeRefs } from '@tunasong/ui-lib'
import cn from 'classnames'
import { useEffect, useRef, useState } from 'react'
import type { ElementRef, HTMLAttributes, Ref } from 'react'
import { drawOverlay, drawWaveform } from './wave-canvas-draw.js'
import type { ColorStop } from './wave-canvas-draw.js'
import { getWaveGradient } from './wave-gradient.js'
import styles from './wave-canvas.module.css'

/** Wave render using a Canvas */
interface WaveProps extends Omit<HTMLAttributes<HTMLDivElement>, 'ref'> {
  className?: string
  /** `fixed`: render each peak one time. `dynamic` fill the available box, calculate peak characteristics  */
  mode?: 'fixed' | 'dynamic'
  peaks?: AudioPeaks
  badge?: string | null
  /** Position in the wave, from 0.0 to 1.0 */
  position?: number
  barWidth?: number
  gapWidth?: number
  barColor?: string
  waveType?: 'audio' | 'monitor'
  ref?: Ref<HTMLCanvasElement>
}

export const WaveCanvas = (props: WaveProps) => {
  const {
    className,
    peaks,
    barWidth = 2,
    gapWidth = 1,
    badge,
    waveType = 'audio',
    position = 0,
    ref: rootRef,
    ...restProps
  } = props
  // tokens is needed for getWaveGradient() and palette.success.light (canvas drawing requires resolved color values)
  const tokens = useDesignTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [containerRef, setContainerRef] = useState<ElementRef<'div'> | null>(null)
  const ref = useMergeRefs(rootRef, canvasRef)

  const size = useContainerSize(containerRef)

  /**
   * Two layers (GH #3163): the bars, and the same bars in the played colour on top, both drawn only when the peaks,
   * size or colours change. A seek or a playing position only moves the top layer's clip, so the bars are never
   * cleared and painted again, which flickered.
   */
  const progressRef = useRef<HTMLCanvasElement>(null)
  const height = size?.height
  const width = size?.width
  /** `useDesignTokens()` is a new object each render, so the effect keys on the resolved colours, not on it */
  const gradientKey = JSON.stringify(getWaveGradient({ tokens, type: waveType }))
  /** @note we cannot use theme.vars here because we do math on the value. This color should be the same in dark and light anyway */
  const playedFill = tokens.palette.success.light
  useEffect(() => {
    const canvas = canvasRef.current
    const progress = progressRef.current
    if (!(canvas && progress && peaks && height && width)) {
      return
    }
    const id = requestAnimationFrame(() => {
      const bars = {
        barWidth,
        gapWidth,
        linePercent: 0.5,
        waveGradient: JSON.parse(gradientKey) as ColorStop[],
        maxVal: 1,
        dataSamples: Array.from(peaks.data),
        height,
        width,
      }
      drawWaveform({ ...bars, canvas })
      drawWaveform({ ...bars, canvas: progress })
      drawOverlay({
        from: 0,
        to: 1,
        fill: playedFill,
        canvas: progress,
      })
    })
    return () => cancelAnimationFrame(id)
  }, [barWidth, gapWidth, gradientKey, height, peaks, playedFill, width])

  const played = Math.min(Math.max(position, 0), 1)

  return (
    <div className={cn(styles.container, className)} style={restProps.style} {...restProps} ref={setContainerRef}>
      {badge ? <div className={styles.badge}>{badge}</div> : null}
      <div className={styles.layers}>
        <canvas ref={ref} className={styles.canvas} />
        <canvas
          ref={progressRef}
          className={cn(styles.canvas, styles.progress)}
          data-wave-layer="progress"
          aria-hidden="true"
          style={{ clipPath: `inset(0 ${+((1 - played) * 100).toFixed(4)}% 0 0)` }}
        />
      </div>
    </div>
  )
}

export default WaveCanvas
